import { readBytes, readString, readUint32, readUint8 } from './buffer';
import { DELTA, LOG_ROUNDS, ROUNDS, SALT_LEN, ZERO_LEN } from './sso-encoder';

interface SSOPackageDecodedResult {
  totalLength?: number;
  version?: number;
  encryption?: number;
  uinType?: number;
  uinLength?: number;
  uin?: string;
  rspHeadLength?: number;
  seqNo?: number;
  retCode?: number;
  retStrLength?: number;
  retStr?: string;
  serviceCmdLength?: number;
  serviceCmd?: string;
  cookieLength?: number;
  cookie?: string;
  flag?: number;
  busiBuffLength?: number;
  busiBuff?: string;
  decrypted?: boolean;
}

export function getBusibuff(data: Uint8Array) {
  const result = decodeSSOPackage(data);
  return result?.busiBuff;
}

function decodeSSOPackage(data: Uint8Array) {
  try {
    const result: SSOPackageDecodedResult = {};
    let offset = 0;

    // 1. 包长度 (4字节)
    result.totalLength = readUint32(data, offset);
    offset += 4;
    // 2. 协议版本 (4字节)
    result.version = readUint32(data, offset);
    offset += 4;
    // 3. 加密算法 (1字节)
    result.encryption = readUint8(data, offset);
    offset += 1;
    // 4. UIN类型 (1字节)
    result.uinType = readUint8(data, offset);
    offset += 1;
    // 5. UIN长度 (4字节)
    result.uinLength = readUint32(data, offset);
    offset += 4;
    // 6. UIN数据
    result.uin = result.uinLength > 4 ? readString(data, offset, result.uinLength - 4) : '';
    offset += result.uinLength - 4;
    const remainingData = data.slice(offset);

    if (result.encryption === 2) {
      const emptyKey = new Uint8Array(16).fill(0);
      const decryptedData = decrypt(
        remainingData,
        emptyKey
      );

      data = decryptedData;

      result.decrypted = true;
      offset = 0;
    } else {
      // 不需要解密
      data = remainingData;
      offset = 0;
    }
    // 7. RspHeadLen (4字节)
    result.rspHeadLength = readUint32(data, offset);
    offset += 4;
    // 8. SeqNo (4字节)
    result.seqNo = readUint32(data, offset);
    offset += 4;
    // 9. RetCode (4字节)
    result.retCode = readUint32(data, offset);
    offset += 4;
    // 10. RetStrLen (4字节)
    result.retStrLength = readUint32(data, offset);
    offset += 4;
    // 11. RetStr
    result.retStr = result.retStrLength ? readString(data, offset, result.retStrLength - 4) : '';
    offset += result.retStrLength - 4;
    // 12. ServiceCmdLen (4字节)
    result.serviceCmdLength = readUint32(data, offset);
    offset += 4;
    // 13. ServiceCmd
    result.serviceCmd = result.serviceCmdLength ? readString(data, offset, result.serviceCmdLength - 4) : '';
    offset += result.serviceCmdLength - 4;
    // 14. CookieLen (4字节)
    result.cookieLength = readUint32(data, offset);
    offset += 4;
    // 15. Cookie
    result.cookie = result.cookieLength ? readString(data, offset, result.cookieLength - 4) : '';
    offset += result.cookieLength - 4;
    // 16. Flag (4字节)
    result.flag = readUint32(data, offset);
    offset += 4;
    // 17. BusiBuffLen (4字节)
    result.busiBuffLength = readUint32(data, offset);
    offset += 4;
    // 18. BusiBuff
    result.busiBuff = result.busiBuffLength ? readString(data, offset, result.busiBuffLength - 4) : '';
    offset += result.busiBuffLength - 4;

    return result;
  } catch (error) {
    // ignore
  }
}

// TEA解密
function teaDecryptBlock(data: Uint8Array, key: Uint8Array) {
  let y = (data[0] << 24) | (data[1] << 16) | (data[2] << 8) | data[3];
  let z = (data[4] << 24) | (data[5] << 16) | (data[6] << 8) | data[7];

  // 确保无符号
  y = y >>> 0;
  z = z >>> 0;

  // 初始sum值
  let sum = (DELTA * ROUNDS) >>> 0;

  // TEA解密循环 - 16轮
  for (let i = 0; i < ROUNDS; i++) {
    z -= ((y << LOG_ROUNDS) + key[2]) ^ (y + sum) ^ ((y >>> 5) + key[3]);
    z = z >>> 0; // 确保无符号
    y -= ((z << LOG_ROUNDS) + key[0]) ^ (z + sum) ^ ((z >>> 5) + key[1]);
    y = y >>> 0; // 确保无符号
    sum -= DELTA;
    sum = sum >>> 0; // 确保无符号
  }

  // 返回解密后的8字节 (网络字节序)
  return new Uint8Array([
    (y >>> 24) & 0xff,
    (y >>> 16) & 0xff,
    (y >>> 8) & 0xff,
    y & 0xff,
    (z >>> 24) & 0xff,
    (z >>> 16) & 0xff,
    (z >>> 8) & 0xff,
    z & 0xff
  ]);
}

function decrypt(inputBuff: Uint8Array, key: Uint8Array) {
  let bufPos = 0;
  const zeroBuf = new Uint8Array(8).fill(0);

  // 解密第一个8字节块
  const firstBlock = new Uint8Array(inputBuff.slice(0, 8));
  let debiBuf = teaDecryptBlock(firstBlock, key);

  // 获取padding长度（只取最低3位）
  const padLen = debiBuf[0] & 0x7;

  // 计算明文长度
  const plainLen = inputBuff.length - 1 - padLen - SALT_LEN - ZERO_LEN;
  const outputBuff = new Uint8Array(plainLen);
  let outputPos = 0;

  let ivPreCrypt = zeroBuf;
  let ivCurCrypt = inputBuff.slice(0, 8);

  bufPos = 8;
  let destI = 1; // 指向debiBuf的下一个位置

  // 跳过padding
  destI += padLen;

  // 跳过salt
  for (let i = 1; i <= SALT_LEN;) {
    if (destI < 8) {
      destI++;
      i++;
    } else if (destI === 8) {
      // 解密新块
      const result = decryptNextBlock(
        inputBuff,
        bufPos,
        ivPreCrypt,
        ivCurCrypt,
        debiBuf,
        key
      );
      ivPreCrypt = result.ivPreCrypt as Uint8Array;
      ivCurCrypt = result.ivCurCrypt as Uint8Array;
      debiBuf = result.debiBuf  as Uint8Array;
      bufPos = result.bufPos as number;
      destI = 0;
    }
  }

  // 提取明文
  let remainingPlain = plainLen;
  while (remainingPlain > 0) {
    if (destI < 8) {
      outputBuff[outputPos++] = debiBuf[destI] ^ ivPreCrypt[destI];
      destI++;
      remainingPlain--;
    } else if (destI === 8) {
      // 解密新块
      const result = decryptNextBlock(
        inputBuff,
        bufPos,
        ivPreCrypt,
        ivCurCrypt,
        debiBuf,
        key
      );
      ivPreCrypt = result.ivPreCrypt as Uint8Array;
      ivCurCrypt = result.ivCurCrypt as Uint8Array;
      debiBuf = result.debiBuf  as Uint8Array;
      bufPos = result.bufPos as number;
      destI = 0;
    }
  }

  // 校验zero部分
  for (let i = 1; i <= ZERO_LEN;) {
    if (destI < 8) {
      if ((debiBuf[destI] ^ ivPreCrypt[destI]) !== 0) {
        // 不抛出错误，继续解密
      }
      destI++;
      i++;
    } else if (destI === 8) {
      if (bufPos >= inputBuff.length) {
        break; // 数据结束
      }
      // 解密新块
      const result = decryptNextBlock(
        inputBuff,
        bufPos,
        ivPreCrypt,
        ivCurCrypt,
        debiBuf,
        key
      );
      if (!result.success) {
        break;
      }
      ivPreCrypt = result.ivPreCrypt as Uint8Array;
      ivCurCrypt = result.ivCurCrypt as Uint8Array;
      debiBuf = result.debiBuf  as Uint8Array;
      bufPos = result.bufPos as number;
      destI = 0;
    }
  }

  return outputBuff;
}

// 辅助方法：解密下一个块
function decryptNextBlock(inputBuff: Uint8Array, bufPos: number, ivPreCrypt: Uint8Array, ivCurCrypt: Uint8Array, debiBuf: Uint8Array, key: Uint8Array) {
  if (bufPos + 8 > inputBuff.length) {
    return { success: false };
  }

  // 更新IV指针
  const newIvPreCrypt = new Uint8Array(ivCurCrypt);
  const newIvCurCrypt = inputBuff.slice(bufPos, bufPos + 8);

  // 先与前一块明文异或
  const blockToDecrypt = new Uint8Array(8);
  for (let j = 0; j < 8; j++) {
    blockToDecrypt[j] = debiBuf[j] ^ newIvCurCrypt[j];
  }

  // TEA解密
  const newDebiBuf = teaDecryptBlock(blockToDecrypt, key);

  return {
    success: true,
    ivPreCrypt: newIvPreCrypt,
    ivCurCrypt: newIvCurCrypt,
    debiBuf: newDebiBuf,
    bufPos: bufPos + 8
  };
}
